/**
 * The policy every declared tool's install meets (spec 2026-10-08 §2). The
 * question is not "is this tool on our list" but "does this install meet the
 * bar", so the policy is three rules and a short override table, and the list
 * of tools is mise's own:
 *
 * - Backends. Only mise's `core` and `aqua` backends verify what they
 *   download (publisher checksums; the aqua registry's per-package checksum).
 *   Every other backend runs a plugin's scripts or skips verification and is
 *   refused by name. mise itself enforces this at install time through
 *   `disable_backends` in the settings file the image bakes, so a tool whose
 *   registry entry names only a refused backend fails to install instead of
 *   running a plugin (verified on the pinned mise: `poetry`, whose backends
 *   are vfox and pypi, fails; `yarn` falls through to `aqua:yarnpkg/berry`).
 * - The lock is the proof. A tool whose `mise.lock` entry carries no checksum
 *   never reaches PATH (`verifyLock` in `toolchainPlan.ts`).
 * - Egress derives from the backend. Core tools have fixed publisher hosts
 *   (`CORE_HOSTS`); aqua tools download from GitHub releases (`AQUA_HOSTS`)
 *   unless an override names the package's own host. A plan opens exactly the
 *   hosts its installs need (`policyHosts`).
 *
 * What the pinned mise (QA_TOOLCHAIN.mise, 2026.9.12) actually does, checked
 * against the release binary before this was written:
 *
 * - There is no `MISE_SETTINGS_FILE` and no `lockfile` setting. Settings come
 *   from a config file's `[settings]` table, so the image bakes one at
 *   `QA_MISE_SETTINGS_PATH` and every run points `MISE_SYSTEM_CONFIG_FILE` at
 *   it. `aqua.registry_floating` and `python.compile` are top-level
 *   `registry_floating` and `all_compile` in this release.
 * - A lock is written by `mise install` only beside a project config whose
 *   `mise.lock` already exists; `mise install <tool>@<version>` with no config
 *   (or with a global one, or after `mise use -g`) writes none. The install
 *   step therefore writes a generated `mise.toml` naming the plan's tools and
 *   an empty `mise.lock` into `<MISE_DATA_DIR>/yagni-toolchain`, outside the
 *   checkout, and runs there with that directory trusted.
 * - `mise install --locked` exists and refuses a tool whose lock entry has no
 *   URL for the platform; `mise lock` writes URLs and checksums without
 *   installing.
 * - The lock is TOML: `[[tools.<name>]]` with `version` and `backend`
 *   (`core:java`, `aqua:kubernetes/kubernetes/kubectl`), then one
 *   `[tools.<name>."platforms.<platform>"]` table per platform with `url`,
 *   optional `url_api`, and `checksum = "sha256:<hex>"` when the backend
 *   verified one. kubectl, java (Temurin), bun, go, node and tflint carry a
 *   checksum; deno, pnpm and yarn (aqua `yarnpkg/berry`) do not.
 * - Hosts: aqua kubectl downloads from dl.k8s.io, not GitHub; core java reads
 *   its metadata from mise-java.jdx.dev and downloads Temurin from GitHub
 *   releases; core deno from dl.deno.land; core go from dl.google.com.
 *   `use_versions_host = false` keeps every install off mise-versions.jdx.dev.
 */
import { QA_ECOSYSTEM_ORIGINS, QA_RUNTIME_DOWNLOAD_HOSTS, QA_RUNTIME_INSTALL_FLOOR } from "./qaToolchain.js";
export const TOOL_POLICY = {
    backends: ["core", "aqua"],
    requireLockChecksum: true,
    installCapMs: 8 * 60_000,
};
/** The tools mise's core backend installs (mise 2026.9.12 `mise registry`). */
export const CORE_TOOLS = [
    "node",
    "python",
    "ruby",
    "go",
    "java",
    "rust",
    "bun",
    "deno",
    "dotnet",
    "elixir",
    "erlang",
    "swift",
    "zig",
];
/** GitHub's release download path: the page, the API, and the two asset hosts it redirects to. */
export const GITHUB_RELEASE_HOSTS = [
    "github.com",
    "api.github.com",
    "objects.githubusercontent.com",
    "release-assets.githubusercontent.com",
];
/** Where each core tool downloads from when mise installs it. */
export const CORE_HOSTS = {
    node: ["nodejs.org"],
    // python-build-standalone's GitHub releases. A run installs Python through
    // uv instead (QA_RUNTIME_DOWNLOAD_HOSTS.python), which pins Astral's mirror.
    python: GITHUB_RELEASE_HOSTS,
    ruby: GITHUB_RELEASE_HOSTS,
    go: ["go.dev", "dl.google.com", "proxy.golang.org", "sum.golang.org"],
    java: ["mise-java.jdx.dev", ...GITHUB_RELEASE_HOSTS],
    rust: ["static.rust-lang.org"],
    bun: GITHUB_RELEASE_HOSTS,
    deno: ["dl.deno.land"],
    dotnet: ["builds.dotnet.microsoft.com", "dotnetcli.azureedge.net"],
    elixir: ["builds.hex.pm", ...GITHUB_RELEASE_HOSTS],
    erlang: GITHUB_RELEASE_HOSTS,
    swift: ["download.swift.org"],
    zig: ["ziglang.org"],
};
/** Every aqua package downloads from GitHub releases unless an override names its own host. */
export const AQUA_HOSTS = GITHUB_RELEASE_HOSTS;
export const TOOL_OVERRIDES = [
    { name: "php", bakedOnly: true },
    { name: "elixir", slow: true },
    { name: "erlang", slow: true },
    { name: "node", floor: QA_RUNTIME_INSTALL_FLOOR.node },
    { name: "python", floor: QA_RUNTIME_INSTALL_FLOOR.python },
    { name: "ruby", floor: QA_RUNTIME_INSTALL_FLOOR.ruby },
    { name: "go", floor: QA_RUNTIME_INSTALL_FLOOR.go },
    { name: "dotnet", floor: QA_RUNTIME_INSTALL_FLOOR.dotnet },
    // mise's registry lists vfox and asdf first for yarn; aqua serves Yarn 2+.
    { name: "yarn", backend: "aqua:yarnpkg/berry", hosts: ["repo.yarnpkg.com"] },
    { name: "kubectl", hosts: ["dl.k8s.io"] },
    { name: "helm", hosts: ["get.helm.sh"] },
];
export function overrideFor(name) {
    return TOOL_OVERRIDES.find((override) => override.name === name);
}
/**
 * The refused backend a tool spec names (`npm:prettier` → "npm"), or null when
 * it names an allowed one or none (a bare name resolves through mise's
 * registry, under the baked `disable_backends`).
 */
export function refusedBackend(spec, policy = TOOL_POLICY) {
    const colon = spec.indexOf(":");
    if (colon <= 0)
        return null;
    const backend = spec.slice(0, colon).toLowerCase();
    return policy.backends.includes(backend) ? null : backend;
}
export function isCoreTool(name) {
    return CORE_TOOLS.includes(name);
}
/**
 * The runtimes a run installs with today's dedicated installers instead of a
 * generic `mise install` (Python through uv, Ruby's pinned precompiled build,
 * Go's toolchain module, .NET's SHA-512-checked tarball, Node through mise
 * from the home). Each already verifies its download, so the lock is not
 * consulted for them. Marked for the cleanup chore with `QaToolchainRuntime`.
 */
export const LEGACY_RUNTIME_TOOLS = ["node", "python", "ruby", "go", "dotnet", "php"];
export function isLegacyRuntimeTool(name) {
    return LEGACY_RUNTIME_TOOLS.includes(name);
}
/** The download hosts one tool's install needs, by how it installs. */
export function installHosts(name, backend) {
    if (isLegacyRuntimeTool(name))
        return [...QA_RUNTIME_DOWNLOAD_HOSTS[name]];
    const override = overrideFor(name);
    if (backend === "core" || backend.startsWith("core:"))
        return isCoreTool(name) ? [...CORE_HOSTS[name]] : [];
    return [...new Set([...AQUA_HOSTS, ...(override?.hosts ?? [])])];
}
/**
 * Every host a plan's installs need, sorted: the egress a sandbox opens for
 * its toolchain. Derived again from the policy tables (`installHosts`), never
 * read from the stored `hosts`: a plan arrives from a saved environment, and a
 * stored host list would let whoever wrote that record widen the sandbox's
 * egress to any host. A tool on a refused backend opens nothing.
 */
export function policyHosts(plan) {
    return [
        ...new Set(plan.tools
            .filter((tool) => tool.origin === "install" && refusedBackend(tool.backend) === null)
            .flatMap((tool) => installHosts(tool.name, tool.backend))),
    ].sort();
}
/** Every host the policy can name, for the test that keeps the tables honest. */
export function everyPolicyHost() {
    return [
        ...new Set([
            ...Object.values(CORE_HOSTS).flat(),
            ...AQUA_HOSTS,
            ...TOOL_OVERRIDES.flatMap((override) => override.hosts ?? []),
            ...Object.values(QA_RUNTIME_DOWNLOAD_HOSTS).flat(),
        ]),
    ].sort();
}
/** The hosts `QA_ECOSYSTEM_ORIGINS` already opens for package installs. */
export function ecosystemHosts() {
    return [
        ...new Set(Object.values(QA_ECOSYSTEM_ORIGINS).flatMap((origins) => origins.map((origin) => new URL(origin).hostname))),
    ].sort();
}
//# sourceMappingURL=toolchainPolicy.js.map